import { FC, useEffect, useState } from 'react';
import { Loader2, X } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { useApprovers } from '@/hooks/queries/use-approvers';
import { useSetApprovers } from '@/hooks/mutations/use-wave-admin';
import { ApprovalSide } from '@/services/workflow/qualification';
import { cn } from '@/lib/utils';

const SIDE_LABEL: Record<ApprovalSide, string> = {
  capitol: 'Capitol',
  client: 'Client',
};

interface SideListProps {
  client: string;
  region: string;
  side: ApprovalSide;
  /** Omitted for the client default itself. */
  waveId?: string;
}

const SideList: FC<SideListProps> = ({ client, region, side, waveId }) => {
  const { data, isLoading } = useApprovers(client, region, side, waveId);
  const save = useSetApprovers();

  const [emails, setEmails] = useState<string[]>([]);
  const [draft, setDraft] = useState('');
  // Only meaningful in a wave. On, the wave has no list of its own and
  // uses the client default; off, it overrides. The list stays visible
  // either way, so you can see who would sign without committing to a copy.
  const [useDefault, setUseDefault] = useState(true);

  useEffect(() => {
    setEmails(data?.emails ?? []);
    setUseDefault(!(data?.is_override ?? false));
  }, [data]);

  const editable = !waveId || !useDefault;

  const add = () => {
    const value = draft.trim().toLowerCase();
    if (!value || emails.includes(value)) {
      setDraft('');
      return;
    }
    setEmails([...emails, value]);
    setDraft('');
  };

  const listChanged =
    JSON.stringify([...emails].sort()) !==
    JSON.stringify([...(data?.emails ?? [])].sort());
  const scopeChanged = !!waveId && useDefault === (data?.is_override ?? false);
  const dirty = scopeChanged || (editable && listChanged);

  if (isLoading) {
    return (
      <div className='flex items-center gap-2 py-4 text-sm text-muted-foreground'>
        <Loader2 className='size-4 animate-spin' />
        Loading {SIDE_LABEL[side]} approvers...
      </div>
    );
  }

  return (
    <div className='space-y-2'>
      <div className='flex items-center justify-between gap-3'>
        <Label>{SIDE_LABEL[side]} side</Label>
        {waveId && (
          <label className='flex items-center gap-2 text-xs text-muted-foreground'>
            Use the client default
            <Switch
              checked={useDefault}
              onCheckedChange={setUseDefault}
              aria-label={`Use the client default ${SIDE_LABEL[side]} approvers`}
            />
          </label>
        )}
      </div>

      {emails.length ? (
        <ul className={cn('flex flex-wrap gap-1.5', !editable && 'opacity-60')}>
          {emails.map((email) => (
            <li
              key={email}
              className='flex items-center gap-1.5 rounded-md bg-muted px-2 py-1 text-sm'
            >
              <span className='font-mono text-xs'>{email}</span>
              {editable && (
                <button
                  type='button'
                  aria-label={`Remove ${email}`}
                  className='text-muted-foreground hover:text-foreground'
                  onClick={() => setEmails(emails.filter((e) => e !== email))}
                >
                  <X className='size-3.5' />
                </button>
              )}
            </li>
          ))}
        </ul>
      ) : (
        <p className='text-sm text-muted-foreground'>
          Nobody. No {SIDE_LABEL[side].toLowerCase()} sign-off can happen until
          somebody is listed here.
        </p>
      )}

      {waveId && useDefault && (
        <p className='text-xs text-muted-foreground'>
          Inherited from {client} in {region}. Switch off to give this wave its
          own list.
        </p>
      )}

      <div className='flex gap-2'>
        {editable && (
          <>
            <Input
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  add();
                }
              }}
              placeholder='name@client.com'
              aria-label={`Add a ${SIDE_LABEL[side]} approver`}
            />
            <Button variant='outline' onClick={add} disabled={!draft.trim()}>
              Add
            </Button>
          </>
        )}
        <Button
          disabled={!dirty || save.isPending}
          onClick={() =>
            save.mutate({
              client,
              region,
              side,
              emails,
              waveId,
              useDefault: !!waveId && useDefault,
            })
          }
          className='gap-2'
        >
          {save.isPending && <Loader2 className='size-4 animate-spin' />}
          Save
        </Button>
      </div>
    </div>
  );
};

interface ApproversPanelProps {
  client: string;
  region: string;
  /** Omitted for the client and region default. */
  waveId?: string;
  waveName?: string;
}

export const ApproversPanel: FC<ApproversPanelProps> = ({
  client,
  region,
  waveId,
  waveName,
}) => (
  <div className='space-y-3 border-t pt-6'>
    <div>
      <h2 className='text-base font-semibold'>
        {waveId ? `Approvers for ${waveName}` : 'Default approvers'}
      </h2>
      <p className='text-sm text-muted-foreground'>
        {waveId
          ? 'Each side either uses the client default or has its own list for this wave. A wave may take its own client reviewers while leaving Capitol on the default.'
          : `Who may sign off at the pre-UAT gate for ${client} in ${region}, unless a wave overrides it. Saving replaces that side's list outright rather than adding to it.`}
      </p>
    </div>

    <div className='grid grid-cols-1 lg:grid-cols-2 gap-6'>
      <SideList
        client={client}
        region={region}
        side='capitol'
        waveId={waveId}
      />
      <SideList client={client} region={region} side='client' waveId={waveId} />
    </div>

    <p className='text-xs text-muted-foreground'>
      Approvers are authorised by email, because a client reviewer may never
      have logged in and so may have no account to point at. Somebody listed on
      both sides is refused outright, so one person cannot settle a gate alone.
      Changes take effect immediately, including on reviews already underway.
    </p>
  </div>
);
